import { useEffect, useState } from "react";
import api, { fmtMXN, fmtDate } from "../lib/api";
import Topbar from "../components/Topbar";
import EmptyState from "../components/EmptyState";
import { Scale, Trophy, ArrowDownAZ } from "lucide-react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

export default function PriceComparator() {
  const { t } = useTranslation();
  const [supplies, setSupplies] = useState([]);
  const [supplyId, setSupplyId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/supplies").then((r) => setSupplies(r.data));
  }, []);

  useEffect(() => {
    if (!supplyId) { setData(null); return; }
    setLoading(true);
    api.get(`/price-comparator/${supplyId}`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [supplyId]);

  return (
    <>
      <Topbar title={t("comparator.title")} subtitle={t("comparator.subtitle")} />
      <div className="p-6 space-y-5">
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <label className="text-xs font-bold uppercase tracking-wider text-[#1A1A1A]">{t("comparator.supplyLabel")}</label>
          <select data-testid="comp-supply-select" value={supplyId} onChange={(e) => setSupplyId(e.target.value)}
            className="mt-1.5 w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]">
            <option value="">{t("comparator.chooseSupply")}</option>
            {supplies.map((s) => <option key={s.id} value={s.id}>{s.name} · {s.unit}</option>)}
          </select>
        </div>

        {loading && <div className="text-center text-sm text-slate-500">{t("comparator.comparing")}</div>}

        {data && (
          data.suppliers.length === 0 ? (
            <EmptyState title={t("comparator.noSuppliersTitle")} message={t("comparator.noSuppliersMsg")} icon={Scale} />
          ) : (
            <div className="space-y-3">
              {data.suppliers.sort((a, b) => a.unit_price - b.unit_price).map((s, idx) => (
                <motion.div key={s.supplier_id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05 }}
                  className={`bg-white rounded-2xl border-2 p-5 ${idx === 0 ? "border-emerald-300" : "border-slate-200"}`}>
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                      {idx === 0 ? (
                        <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                          <Trophy className="w-5 h-5 text-emerald-700" />
                        </div>
                      ) : (
                        <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                          <ArrowDownAZ className="w-5 h-5 text-slate-600" />
                        </div>
                      )}
                      <div>
                        <div className="font-display font-bold text-base text-[#1A1A1A]">{s.business_name}</div>
                        {idx === 0 && (
                          <span className="inline-block mt-0.5 px-2 py-0.5 bg-emerald-500 text-white text-[10px] font-bold rounded-full">
                            {t("comparator.bestPrice")}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`font-display font-extrabold text-2xl ${idx === 0 ? "text-emerald-700" : "text-[#1A1A1A]"}`}>
                        {fmtMXN(s.unit_price)}
                      </div>
                      <div className="text-xs text-slate-500">{t("common.perUnit")}</div>
                    </div>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <div className="text-[10px] font-bold text-slate-500 uppercase">{t("comparator.lastUpdate")}</div>
                      <div className="text-slate-700 font-semibold">{fmtDate(s.last_updated)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] font-bold text-slate-500 uppercase">{t("comparator.authorizedAt")}</div>
                      <div className="text-slate-700 font-semibold">{s.branches_count} suc.</div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )
        )}
      </div>
    </>
  );
}
